// src/components/pages/posts/blog/BlogListGeneric.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSnackbar } from 'notistack';
import Cookies from 'js-cookie';
import { useAuth } from '../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL;

const BlogListGeneric = ({ postType, heading }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { authData, apiFetch, isSso } = useAuth(); // <-- use apiFetch + isSso
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postType]);

  const fetchPosts = async () => {
    try {
      const res = await apiFetch(`${API_BASE}/api/upload_post/`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const allPosts = await res.json();

      const filteredPosts = (allPosts || []).filter((p) => p.post_type === postType);
      setPosts(filteredPosts);
    } catch (error) {
      console.error(`Error fetching ${postType} posts:`, error);
      enqueueSnackbar(`Failed to load ${heading} posts. Please try again.`, {
        variant: 'error',
      });
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      const headers = {};
      // CSRF only matters for legacy cookie auth; SSO (Bearer) doesn't need it.
      if (!isSso) {
        headers['X-CSRFToken'] = Cookies.get('csrftoken');
      }

      const res = await apiFetch(`${API_BASE}/api/upload_post/${postId}/`, {
        method: 'DELETE',
        headers,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }

      enqueueSnackbar('Post deleted successfully.', { variant: 'success' });
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
      enqueueSnackbar('Failed to delete post.', { variant: 'error' });
    }
  };

  const isImage = (fileUrl = '') => /\.(jpe?g|png|gif)$/i.test(fileUrl);
  const isPdf   = (fileUrl = '') => /\.pdf$/i.test(fileUrl);
  const isPpt   = (fileUrl = '') => /\.(pptx?|ppsx?)$/i.test(fileUrl);

  return (
    <Box sx={{ padding: 4 }}>
      <Typography
        variant="h3"
        sx={{ mt: 4, mb: 4, color: 'primary.main', textAlign: 'center' }}
      >
        {heading}
      </Typography>

      {posts.map((post) => (
        <Card key={post.id} sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography
              variant="h5"
              sx={{ color: 'primary.main', fontWeight: 'bold' }}
            >
              {post.title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {post.date}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.primary' }}>
              {post.description}
            </Typography>
          </CardContent>

          {post.file && (
            <Box sx={{ p: 2 }}>
              <Button
                variant="outlined"
                onClick={() => window.open(post.file_url, '_blank', 'noopener,noreferrer')}
                sx={{ mr: 1, mb: 2 }}
              >
                Download File
              </Button>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>View</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {isImage(post.file) && (
                    <Box
                      component="img"
                      src={post.file_url}
                      alt="Attached"
                      sx={{
                        maxWidth: '100%',
                        height: 'auto',
                        display: 'block',
                        margin: '0 auto',
                      }}
                    />
                  )}

                  {isPdf(post.file) && (
                    <Box sx={{ position: 'relative', width: '100%', height: '500px', mb: 2 }}>
                      <iframe
                        src={post.file_url}
                        title="PDF Preview"
                        style={{ width: '100%', height: '100%', border: 'none' }}
                      />
                    </Box>
                  )}

                  {isPpt(post.file) && (
                    <Button
                      variant="outlined"
                      onClick={() => window.open(post.file_url, '_blank', 'noopener,noreferrer')}
                      sx={{ mr: 1 }}
                    >
                      Download PowerPoint
                    </Button>
                  )}

                  {!isImage(post.file) && !isPdf(post.file) && !isPpt(post.file) && (
                    <Button
                      variant="outlined"
                      onClick={() => window.open(post.file_url, '_blank', 'noopener,noreferrer')}
                    >
                      View/Download File
                    </Button>
                  )}
                </AccordionDetails>
              </Accordion>
            </Box>
          )}

          {authData?.is_staff && (
            <Box sx={{ p: 2, textAlign: 'right' }}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleDeletePost(post.id)}
              >
                Delete Post
              </Button>
            </Box>
          )}
        </Card>
      ))}
    </Box>
  );
};

export default BlogListGeneric;